<template>
    <div class="w-full" :class="[{ 'cursor-zoom-out': zoomState }, 'relative']" @click="handleZoom">
        <div v-show="zoomState" class="absolute top-0 bottom-0 right-0 left-0" :style="zoomState ? `background-position: ${zoomSettings.imagePosX}% ${zoomSettings.imagePosY}%;
        background-image: url(${imageSrc}); background-repeat: no-repeat; background-size: ${zoomSize}px;` : ''"></div>
        <nuxt-img :src="imageSrc" :loading="loading" :preset="preset" :provider="provider" :alt="alt" :class="[
        'w-full block object-center object-cover',
        {
            'min-w-0 grow shrink-0 basis-0': type === IMAGE_TYPE.flex,
            'aspect-square': type === IMAGE_TYPE.square,
        },
        { 'opacity-0': zoomState },
        { 'cursor-zoom-in': isZoomable && !zoomState },
    ]" @load="emitLoad" ref="imageReference" @mousemove="onMouseEvent" @mouseenter="onMouseEvent" />
    </div>
</template>

<script setup lang="ts">
import type { Props } from './props'
import { IMAGE_TYPE } from './image'
import { DEFAULT_SRC } from './image.mock'
import type { NuxtImg } from '#build/components';

const props = withDefaults(defineProps<Props>(), {
    type: undefined,
})

const emit = defineEmits<{
    (e: 'imgLoaded', height: number): void
}>()

const zoomState = ref(false)
const imageReference = ref<ComponentPublicInstance | null>(null)
const imageSrc = computed(() => props.src || DEFAULT_SRC)
const zoomSettings = reactive({
    maxX: 0,
    maxY: 0,
    minX: 0,
    minY: 0,
    imagePosX: 0,
    imagePosY: 0,
})
const zoomSize = ref(0)

function emitLoad(e: Event) {
    if (!props.isEmitLoad) {
        return
    }

    if (e && e.target) {
        emit('imgLoaded', (e.target as HTMLImageElement).height)
    }
}

async function handleZoom(e: MouseEvent) {
    if (!props.isZoomable) {
        return;
    }

    if (zoomState.value) {
        zoomSize.value = 0;
        zoomState.value = false
        return;
    }

    await nextTick()
    if (!imageReference.value) {
        return;
    }

    const imageSizes = (imageReference.value?.$el as HTMLElement)?.getBoundingClientRect()

    zoomSize.value = imageSizes.height * 2

    zoomSettings.minX = imageSizes.left
    zoomSettings.minY = imageSizes.top
    zoomSettings.maxX = imageSizes.right
    zoomSettings.maxY = imageSizes.bottom

    zoomState.value = true
    setImagePosition(e)
}

function setImagePosition(e: MouseEvent) {
    const imagePosX = ((e.x - zoomSettings.minX) + (0.25 * e.x)) / zoomSettings.maxX * 100
    const imagePosY = e.y / zoomSettings.maxY * 100

    zoomSettings.imagePosX = imagePosX <= 100 ? imagePosX : 100
    zoomSettings.imagePosY = imagePosY <= 100 ? imagePosY : 100
}

function onMouseEvent(e: MouseEvent) {
    if (!props.isZoomable || !zoomState.value) {
        return;
    }

    setImagePosition(e)
}
</script>
